'use client';

import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Maximize2 } from 'lucide-react';
import type { WorkshopVideo } from '@/lib/workshops/types';
import MediaPlaceholder from './MediaPlaceholder';

/* One-time CSS: equalizer + line clamps. Gated to the client; reduced-motion
   freezes the eq bars to a static frame. */
function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('wk-video-styles')) return;
  const s = document.createElement('style');
  s.id = 'wk-video-styles';
  s.textContent = `
  @keyframes wk-eq { 0%,100%{transform:scaleY(.35)} 50%{transform:scaleY(1)} }
  .wk-eqbar{transform-origin:bottom;animation:wk-eq 900ms ease-in-out infinite}
  @media (prefers-reduced-motion: reduce){ .wk-eqbar{animation:none;transform:scaleY(.7)} }
  .wk-clamp2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
  .wk-clamp3{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
  `;
  document.head.appendChild(s);
}

/* ── Shared single-video manager: at most one video active (playing) and at
   most one unmuted across the whole page. A global singleton so every
   VideoUnit instance coordinates. ── */
type Ctrl = {
  id: string;
  play: () => void;
  pause: () => void;
  mute: () => void;
  unmute: () => void;
};
class VideoManager {
  registry = new Map<string, Ctrl>();
  activeId: string | null = null;
  unmutedId: string | null = null;
  subs = new Set<() => void>();
  register(c: Ctrl) { this.registry.set(c.id, c); }
  unregister(id: string) {
    this.registry.delete(id);
    if (this.activeId === id) this.activeId = null;
    if (this.unmutedId === id) this.unmutedId = null;
    this.emit();
  }
  subscribe(fn: () => void) { this.subs.add(fn); return () => { this.subs.delete(fn); }; }
  emit() { this.subs.forEach((fn) => fn()); }
  enterView(id: string) {
    if (this.activeId && this.activeId !== id) this.registry.get(this.activeId)?.pause();
    this.activeId = id;
    this.registry.get(id)?.play();
    this.emit();
  }
  leaveView(id: string) {
    if (this.activeId === id) { this.registry.get(id)?.pause(); this.activeId = null; }
    if (this.unmutedId === id) { this.registry.get(id)?.mute(); this.unmutedId = null; }
    this.emit();
  }
  unmute(id: string) {
    this.registry.forEach((c, k) => { if (k !== id) c.mute(); });
    this.unmutedId = id;
    this.enterView(id);
    this.registry.get(id)?.unmute();
    this.emit();
  }
  mute(id: string) {
    if (this.unmutedId === id) this.unmutedId = null;
    this.registry.get(id)?.mute();
    this.emit();
  }
  isUnmuted(id: string) { return this.unmutedId === id; }
}
function getManager(): VideoManager {
  const g = globalThis as any;
  if (!g.__wkVideoManager) g.__wkVideoManager = new VideoManager();
  return g.__wkVideoManager as VideoManager;
}

function parseEmbedId(source: 'YouTube' | 'Vimeo', raw: string): string {
  const v = (raw || '').trim();
  if (source === 'YouTube') {
    const m =
      v.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/) ||
      v.match(/^([\w-]{11})$/);
    return m ? m[1] : v;
  }
  const m = v.match(/(\d{6,})/);
  return m ? m[1] : v;
}

function embedSrc(video: WorkshopVideo): string {
  const id = parseEmbedId(video.source, video.id);
  if (video.source === 'YouTube') {
    return (
      `https://www.youtube.com/embed/${id}` +
      `?enablejsapi=1&autoplay=1&mute=1&controls=0&loop=1&playlist=${id}` +
      `&playsinline=1&modestbranding=1&rel=0`
    );
  }
  return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1&loop=1&controls=0&playsinline=1`;
}

function EqBars({ large }: { large: boolean }) {
  const h = large ? 12 : 10;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: h }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="wk-eqbar"
          style={{ width: 2.5, height: h, background: '#fff', borderRadius: 2, animationDelay: `${i * 160}ms` }}
        />
      ))}
    </span>
  );
}

export default function VideoUnit({
  post,
  size = 'compact',
  onPlay,
  onUnmute,
}: {
  post: { id: string; video: WorkshopVideo };
  size?: 'compact' | 'large';
  onPlay?: () => void;
  onUnmute?: () => void;
}) {
  const large = size === 'large';
  const uid = `${post.id}:${size}`;
  const v = post.video;
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playedOnceRef = useRef(false);
  const [sound, setSound] = useState(false);
  const manager = getManager();

  useEffect(() => { injectStyles(); }, []);

  // Register an iframe-control adapter with the manager + keep local sound in
  // sync when another unit steals the unmute slot.
  useEffect(() => {
    const send = (msg: Record<string, unknown>) => {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(msg), '*');
    };
    const yt = v.source === 'YouTube';
    const ctrl: Ctrl = {
      id: uid,
      play: () => send(yt ? { event: 'command', func: 'playVideo', args: [] } : { method: 'play' }),
      pause: () => send(yt ? { event: 'command', func: 'pauseVideo', args: [] } : { method: 'pause' }),
      mute: () => send(yt ? { event: 'command', func: 'mute', args: [] } : { method: 'setVolume', value: 0 }),
      unmute: () => send(yt ? { event: 'command', func: 'unMute', args: [] } : { method: 'setVolume', value: 1 }),
    };
    manager.register(ctrl);
    const unsub = manager.subscribe(() => setSound(manager.isUnmuted(uid)));
    return () => {
      manager.unregister(uid);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, v.source]);

  // Autoplay (muted) only while in viewport; pause off-screen. Counts one play.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            manager.enterView(uid);
            if (!playedOnceRef.current) {
              playedOnceRef.current = true;
              onPlay?.();
            }
          } else {
            manager.leaveView(uid);
          }
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const toggleSound = () => {
    if (manager.isUnmuted(uid)) {
      manager.mute(uid);
    } else {
      manager.unmute(uid);
      onUnmute?.();
    }
  };

  const goFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    containerRef.current?.requestFullscreen?.();
  };

  return (
    <div
      ref={containerRef}
      onClick={toggleSound}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSound();
        }
      }}
      aria-label={sound ? 'Mute video' : 'Unmute video'}
      className="relative w-full overflow-hidden rounded-md bg-surface-3 cursor-pointer select-none"
      style={{ aspectRatio: '16 / 9' }}
    >
      {/* Fallback stripes behind the iframe (visible until embed paints). */}
      <div className="absolute inset-0">
        <MediaPlaceholder label={`${v.source} embed`} icon="video" />
      </div>

      <iframe
        ref={iframeRef}
        src={embedSrc(v)}
        title={`${v.source} preview`}
        className="absolute inset-0 w-full h-full pointer-events-none border-0"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
      />

      {/* top scrim */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(8,10,18,.55) 0%, rgba(8,10,18,0) 60%)' }}
      />

      {/* live badge top-left */}
      <div className="absolute flex items-center gap-1.5" style={{ top: large ? 12 : 8, left: large ? 12 : 8 }}>
        <span
          className="font-mono inline-flex items-center gap-1.5 text-white uppercase"
          style={{
            height: large ? 24 : 20,
            padding: large ? '0 9px' : '0 7px',
            borderRadius: 'var(--r-sm, 6px)',
            background: 'rgba(8,10,18,.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            fontSize: large ? 10 : 9,
            fontWeight: 600,
            letterSpacing: '.05em',
          }}
        >
          <EqBars large={large} /> {sound ? 'Playing' : 'Muted autoplay'}
        </span>
      </div>

      {/* large-only center affordance when muted */}
      {large && !sound && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 pointer-events-none">
          <div
            className="rounded-full bg-accent text-text-on-accent grid place-items-center"
            style={{ width: 56, height: 56, boxShadow: '0 8px 28px rgba(8,10,18,.4)' }}
          >
            <VolumeX className="w-6 h-6" strokeWidth={2} />
          </div>
          <span className="text-white text-[13px] font-semibold" style={{ textShadow: '0 1px 6px rgba(8,10,18,.6)' }}>
            Tap to play with sound
          </span>
        </div>
      )}

      {/* bottom scrim + controls */}
      <div
        className="absolute left-0 right-0 bottom-0"
        style={{
          background: 'linear-gradient(0deg, rgba(8,10,18,.72) 0%, rgba(8,10,18,0) 46%)',
          padding: large ? '0 12px 11px' : '0 8px 8px',
        }}
      >
        {!large ? (
          <div className="flex items-center" style={{ marginBottom: 7 }}>
            <span
              className="inline-flex items-center gap-1.5 text-white"
              style={{
                height: 24,
                padding: '0 9px',
                borderRadius: 'var(--r-full, 9999px)',
                background: 'rgba(8,10,18,.6)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {sound ? <Volume2 className="w-3.5 h-3.5" strokeWidth={2} /> : <VolumeX className="w-3.5 h-3.5" strokeWidth={2} />}
              {sound ? 'Sound on' : 'Tap for sound'}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3" style={{ marginBottom: 9 }}>
            <span
              className="inline-flex items-center gap-1.5 bg-accent text-text-on-accent"
              style={{ height: 28, padding: '0 12px', borderRadius: 'var(--r-full, 9999px)', fontSize: 12.5, fontWeight: 600 }}
            >
              {sound ? <Volume2 className="w-3.5 h-3.5" strokeWidth={2} /> : <VolumeX className="w-3.5 h-3.5" strokeWidth={2} />}
              {sound ? 'Sound on — tap to mute' : 'Unmute'}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={goFullscreen}
              aria-label="Fullscreen"
              className="text-white/85 hover:text-white"
            >
              <Maximize2 className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        )}

        {/* progress track (decorative in v1) */}
        <div
          className="relative rounded-full"
          style={{ height: large ? 4 : 3, background: 'rgba(255,255,255,.28)' }}
        >
          <div
            className="absolute left-0 top-0 bottom-0 bg-accent rounded-full"
            style={{ width: sound ? '34%' : '8%' }}
          />
        </div>
      </div>
    </div>
  );
}
